// Pure helpers for the slot-definitions heatmap. No React, no side effects —
// kept this way so the geometry can be unit-tested later if a runner is added.
//
// The heatmap places every slot row on a vertical day axis (toggleable between
// working hours and a full 24h day) and a horizontal weekday axis whose first
// column honors the configured first-day-of-week (see WEEKDAY_COLUMNS). This
// module owns that geometry plus the deterministic per-entity hue used for
// doctor avatars and tenant tags.

import { APP_CONFIG } from "@/lib/config/app-config";

export const DAY_START_MIN = 7 * 60; // 07:00
export const DAY_END_MIN = 21 * 60; // 21:00

// The cell day-axis toggles between the working-hours window and a full 24h
// view — clicking the hour-ruler legend in the matrix header switches it.
export const DAY_SCALES = {
  work: {
    startMin: DAY_START_MIN,
    endMin: DAY_END_MIN,
    legend: "7a · · 10a · · 1p · · 4p · · 7p · 9p",
    gridHours: [10, 12, 14, 16, 18], // DayColumn horizontal grid lines
    rulerHours: [9, 12, 15, 18], // HourRulerMini vertical ticks
  },
  full: {
    startMin: 0,
    endMin: 24 * 60,
    legend: "12a · · 6a · · 12p · · 6p · · 12a",
    gridHours: [4, 8, 12, 16, 20],
    rulerHours: [6, 12, 18],
  },
};

// ISO-ordered base labels (1=Mon … 7=Sun). Index these by `isoWeekday - 1`
// (the detail rail does exactly this with the active cell's ISO weekday).
export const WEEKDAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];
export const WEEKDAY_LONG = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// The configured first day of the week, normalized to an ISO weekday (1=Mon …
// 7=Sun). App config uses the date-fns convention (0=Sun … 6=Sat); both 0 and 7
// mean Sunday here.
const RAW_FIRST_DAY = APP_CONFIG?.WEEK_STARTS_ON ?? 1;
export const FIRST_ISO_WEEKDAY =
  RAW_FIRST_DAY === 0 || RAW_FIRST_DAY === 7 ? 7 : RAW_FIRST_DAY;

// The seven weekday columns in DISPLAY order, rotated to begin on the configured
// first day. Each column carries its ISO weekday (for slot-data lookups), its
// short header letter, and whether it's a weekend day (for shading). Iterate
// THIS — not the raw ISO arrays — wherever the heatmap renders weekday columns,
// so the grid, headers, and week-strip all honor the first-day-of-week setting.
export const WEEKDAY_COLUMNS = Array.from({ length: 7 }, (_, idx) => {
  const iso = ((FIRST_ISO_WEEKDAY - 1 + idx) % 7) + 1;
  return {
    iso,
    letter: WEEKDAY_LETTERS[iso - 1],
    long: WEEKDAY_LONG[iso - 1],
    weekend: iso === 6 || iso === 7,
  };
});

// The weekday columns actually rendered. With `hideSunday` (a local preference
// for clinics closed on Sundays) the Sunday column (ISO 7) is dropped; the rest
// keep their configured first-day order.
export function getVisibleWeekdayColumns(hideSunday = false) {
  return hideSunday ? WEEKDAY_COLUMNS.filter((col) => col.iso !== 7) : WEEKDAY_COLUMNS;
}

// Minutes-since-midnight for a Luxon DateTime. Used uniformly for static and
// recurring rows — a recurring row's `startAt` is its first in-range occurrence
// and carries the correct time-of-day.
export function minutesOfDay(dt) {
  if (!dt || typeof dt.hour !== "number") return 0;
  return dt.hour * 60 + dt.minute;
}

// Midday cutoff (13:00) that splits a day into AM and PM sessions: a slot
// starting strictly before 13:00 is "AM", one starting at/after 13:00 is "PM".
// Kept as one constant so the rail's quick-select and the page's selection
// handler agree on the boundary.
export const SESSION_SPLIT_MIN = 13 * 60; // 13:00

// "am" | "pm" for a slot by its start time-of-day, or null when the row has no
// usable start. Recurring rows carry their first in-range occurrence in
// `startAt`, so `minutesOfDay` reads the right clock time for static and
// recurring rows alike.
export function slotSession(slot) {
  const dt = slot?.startAt;
  if (!dt || typeof dt.hour !== "number") return null;
  return minutesOfDay(dt) < SESSION_SPLIT_MIN ? "am" : "pm";
}

// Vertical placement of a slot band inside a day cell, as percentages of the
// 7am–9pm axis. `topPct` is clamped to the axis; `heightPct` has a 2.4% floor so
// even a 10-minute slot stays visible. Pure + unit-testable.
export function dayCellGeometry(slot, dayStart = DAY_START_MIN, dayEnd = DAY_END_MIN) {
  const span = dayEnd - dayStart || 1;
  const startMin = minutesOfDay(slot?.startAt);
  const endMin = minutesOfDay(slot?.endAt);
  const rawTop = ((startMin - dayStart) / span) * 100;
  const rawHeight = ((endMin - startMin) / span) * 100;
  const topPct = Math.min(100, Math.max(0, rawTop));
  const heightPct = Math.max(2.4, rawHeight);
  return { topPct, heightPct };
}

// Which weekday column(s) a slot row belongs to, as ISO weekday numbers (1=Mon).
// Recurring slot definitions carry an explicit `recurrence.weekdays` mask (the
// slot-definitions endpoint returns it inline). Rows without a mask — static
// slots, and the live "available" view's concrete occurrences — fall back to
// the single weekday of their representative start.
export function slotWeekdays(row) {
  const mask = row?.recurrence?.weekdays;
  if (Array.isArray(mask) && mask.length > 0) {
    return mask.filter((iso) => Number.isInteger(iso) && iso >= 1 && iso <= 7);
  }
  const iso = row?.startAt?.weekday;
  return Number.isInteger(iso) && iso >= 1 && iso <= 7 ? [iso] : [];
}

// 5-bucket week-of-month mask for monthly Nth-weekday slots, in display order
// [W1, W2, W3, W4, Last]. Returns null for anything else (static, weekly,
// monthly day-of-month, dates) — those keep the existing solid-bar render.
// The Last bucket (-1) is its own segment and never folds into W4; mirrors
// `MONTH_WEEK_LABELS` in slot-form-sheet.jsx so the form pills and the heatmap
// bar share one interpretation.
export function monthlyWeekSegments(row) {
  const rec = row?.recurrence;
  if (!rec || rec.frequency !== "monthly") return null;
  const weeks = Array.isArray(rec.monthWeeks) ? rec.monthWeeks : null;
  if (!weeks || weeks.length === 0) return null;
  const mask = [false, false, false, false, false];
  weeks.forEach((n) => {
    if (n >= 1 && n <= 4) mask[n - 1] = true;
    else if (n === -1) mask[4] = true;
  });
  return mask;
}

// Deterministic OKLCH hue (0–359) from an entity id. djb2 hash → degree, so a
// doctor/tenant keeps a stable color across sessions without any seeding.
export function hashHue(id) {
  const str = String(id || "");
  let hash = 5381;
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
}

// Normalize a loosely-typed color value to a lowercase `#rrggbb` string, or ""
// when it isn't a usable hex (`#rgb` shorthand is expanded).
export function normalizeHex(value) {
  const input = typeof value === "string" ? value.trim() : "";
  if (!input) return "";
  const body = input.startsWith("#") ? input.slice(1) : input;
  if (/^[0-9a-fA-F]{3}$/.test(body)) {
    return `#${body
      .split("")
      .map((char) => `${char}${char}`)
      .join("")
      .toLowerCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(body)) return `#${body.toLowerCase()}`;
  return "";
}

// The fill color for a slot band: the slot's own `colorHex` when it's a usable
// hex, otherwise the location's hue. Unset, malformed, and pure-black values
// all fall through to the hue so bands are never black or invisible.
export function slotColor(row, tenantHue) {
  const hex = normalizeHex(row?.colorHex);
  if (hex && hex !== "#000000") return hex;
  return `oklch(0.62 0.16 ${tenantHue})`;
}

// The `colorHex` shared by every row in the list, or "" when the rows are
// uncolored or use a mix of colors. Used to pre-fill the rail's bulk-color
// control with the current color.
export function deriveSharedColor(rows) {
  const colors = (rows || [])
    .map((row) => normalizeHex(row?.colorHex))
    .filter(Boolean);
  if (colors.length === 0) return "";
  return colors.every((color) => color === colors[0]) ? colors[0] : "";
}

// "HH:mm–HH:mm" for a pair of Luxon DateTimes. Lifted from day-view-slots-panel.
export function formatRange(startAt, endAt) {
  if (!startAt?.toFormat || !endAt?.toFormat) return "";
  return `${startAt.toFormat("HH:mm")}–${endAt.toFormat("HH:mm")}`;
}

// "30m" / "1h" / "1h 15m" for a pair of Luxon DateTimes. Lifted from
// day-view-slots-panel.
export function formatDuration(startAt, endAt) {
  if (!startAt?.diff || !endAt?.diff) return "";
  const minutes = Math.round(endAt.diff(startAt, "minutes").minutes);
  if (minutes <= 0) return "";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours}h ${remaining}m` : `${hours}h`;
}
